/**
 * Resolves with `promise`'s value, or `undefined` if it doesn't settle within `ms`. Used to guard
 * the storage-clearing calls below: WebKit's IndexedDB is known to hang on `open()`/delete (never
 * firing success/error) — without a timeout a logout/recovery could block forever. Resolve-based
 * (not reject-based) so callers never need a try/catch around it.
 */
export function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T | void> {
  return Promise.race([promise, new Promise<void>((resolve) => setTimeout(resolve, ms))])
}

/**
 * Best-effort wipe of all client-side storage for this origin: `localStorage`, `sessionStorage`,
 * service worker registrations, the Cache API, and all IndexedDB databases (including the Apollo
 * `{user_id}-store` / `{user_id}-offline` stores). Web storage holds per-device UI state (dashboard
 * filters, report state, etc.) that would otherwise survive a "clear app data & reload", so it's
 * wiped too — a genuine fresh start. The async steps are each wrapped in `withTimeout` so a hung
 * IndexedDB — the very thing this guards against — can't block the caller, and they run concurrently
 * so the worst case is one `timeoutMs`, not their sum. Does NOT reload or redirect; callers decide
 * what to do afterward (logout redirect, reload, etc.).
 *
 * `skipLocalStorage` leaves `localStorage` untouched for callers that have already cleared it
 * selectively — logout runs `clearUserData` first, which keeps a whitelist of per-device UI
 * preferences (see `clearLocalStorage`), and an unconditional `clear()` here would silently undo
 * that whitelist. `sessionStorage` and the async stores are still wiped either way.
 */
export async function clearClientStorage({
  timeoutMs = 3000,
  skipLocalStorage = false
}: { timeoutMs?: number; skipLocalStorage?: boolean } = {}): Promise<void> {
  // Synchronous web-storage wipe. Each is isolated in its own try/catch so a SecurityError on one
  // (e.g. storage disabled) can't abort the other or the async wipe below.
  if (!skipLocalStorage) {
    try {
      window.localStorage?.clear()
    } catch {
      // ignore — best-effort
    }
  }
  try {
    window.sessionStorage?.clear()
  } catch {
    // ignore — best-effort
  }

  const clearServiceWorkers = async () => {
    if (!('serviceWorker' in navigator)) return
    await navigator.serviceWorker
      .getRegistrations()
      .then((registrations) => Promise.all(registrations.map((r) => r.unregister())))
  }

  const clearCaches = async () => {
    if (typeof caches === 'undefined') return
    await caches.keys().then((keys) => Promise.all(keys.map((k) => caches.delete(k))))
  }

  const clearIndexedDb = async () => {
    if (!window.indexedDB?.databases) return
    const deleteDatabase = (name: string) =>
      new Promise<void>((resolve) => {
        const req = window.indexedDB.deleteDatabase(name)
        // resolve on any terminal state — a blocked/errored delete shouldn't hold up the caller
        req.onsuccess = req.onerror = req.onblocked = () => resolve()
      })
    await window.indexedDB
      .databases()
      .then((dbs) => Promise.all(dbs.map((db) => (db.name ? deleteDatabase(db.name) : Promise.resolve()))))
  }

  // Run concurrently; each is independently time-boxed and swallows its own errors so one hung
  // store can't block the others or reject the whole wipe.
  await Promise.all(
    [clearServiceWorkers(), clearCaches(), clearIndexedDb()].map((p) => withTimeout(p, timeoutMs).catch(() => {}))
  )
}
